import { useEffect, useState, useCallback } from "react";
import { Loader2, Search, Check, Trash2, RefreshCcw } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "@/lib/api";
import EmptyState from "@/components/admin/EmptyState";
import ConfirmDialog from "@/components/admin/ConfirmDialog";

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}

export default function AdminContacts() {
  const [tab, setTab] = useState("contacts"); // 'contacts' | 'signups'
  const [contacts, setContacts] = useState([]);
  const [signups, setSignups] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all | open | resolved
  const [loading, setLoading] = useState(true);
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (tab === "contacts") {
        const params = {};
        if (q) params.q = q;
        if (statusFilter === "resolved") params.resolved = true;
        if (statusFilter === "open") params.resolved = false;
        const rows = await adminApi.listContacts(params);
        setContacts(rows);
      } else {
        const rows = await adminApi.listSignups(q ? { q } : {});
        setSignups(rows);
      }
    } catch (e) {
      toast.error("Couldn't load list.");
    } finally {
      setLoading(false);
    }
  }, [tab, q, statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleResolved = async (row) => {
    try {
      const updated = await adminApi.resolveContact(row.id, !row.resolved);
      setContacts((prev) => prev.map((r) => (r.id === row.id ? updated : r)));
      toast.success(updated.resolved ? "Marked resolved." : "Reopened.");
    } catch {
      toast.error("Couldn't update.");
    }
  };

  const doDelete = async () => {
    if (!confirm) return;
    try {
      if (confirm.type === "contact") {
        await adminApi.deleteContact(confirm.id);
        setContacts((prev) => prev.filter((r) => r.id !== confirm.id));
      } else {
        await adminApi.deleteSignup(confirm.id);
        setSignups((prev) => prev.filter((r) => r.id !== confirm.id));
      }
      toast.success("Deleted.");
    } catch {
      toast.error("Couldn't delete.");
    } finally {
      setConfirm(null);
    }
  };

  const isContacts = tab === "contacts";
  const rows = isContacts ? contacts : signups;

  return (
    <div data-testid="page-admin-contacts" className="space-y-6">
      {/* Tabs */}
      <div className="flex flex-wrap items-center gap-2">
        <TabButton active={tab === "contacts"} onClick={() => setTab("contacts")} testid="tab-contacts">
          Contact Requests
        </TabButton>
        <TabButton active={tab === "signups"} onClick={() => setTab("signups")} testid="tab-signups">
          Challenge Signups
        </TabButton>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search size={14} className="absolute left-4 top-1/2 -translate-y-1/2" style={{ color: "#6E6759" }} />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={isContacts ? "Search name, email, message…" : "Search name or email…"}
            data-testid="contacts-search"
            className="w-full rounded-full border pl-10 pr-4 py-2 text-sm outline-none"
            style={{ background: "#fff", borderColor: "rgba(27,67,50,0.15)", color: "#2A2621" }}
          />
        </div>

        {isContacts && (
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            data-testid="contacts-status-filter"
            className="rounded-full border px-4 py-2 text-sm"
            style={{ background: "#fff", borderColor: "rgba(27,67,50,0.15)", color: "#2A2621" }}
          >
            <option value="all">All</option>
            <option value="open">Open</option>
            <option value="resolved">Resolved</option>
          </select>
        )}

        <button type="button" onClick={load} className="btn-outline-forest" data-testid="contacts-refresh">
          <RefreshCcw size={14} /> Refresh
        </button>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex items-center gap-3" style={{ color: "#1B4332" }}>
          <Loader2 className="animate-spin" size={16} /> Loading&hellip;
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          title={isContacts ? "No contact requests" : "No signups yet"}
          subtitle={isContacts ? "Notes people leave through the site will appear here." : "The 7-day challenge signups will appear here."}
          testid="contacts-empty"
        />
      ) : (
        <div className="grid gap-4">
          {rows.map((row) =>
            isContacts ? (
              <ContactCard
                key={row.id}
                row={row}
                onResolve={() => toggleResolved(row)}
                onDelete={() => setConfirm({ type: "contact", id: row.id, label: row.name })}
              />
            ) : (
              <SignupCard
                key={row.id}
                row={row}
                onDelete={() => setConfirm({ type: "signup", id: row.id, label: row.name })}
              />
            )
          )}
        </div>
      )}

      <ConfirmDialog
        open={!!confirm}
        title="Delete this entry?"
        message={confirm ? `“${confirm.label}” will be removed permanently.` : ""}
        confirmLabel="Delete"
        destructive
        onClose={() => setConfirm(null)}
        onConfirm={doDelete}
        testid="contacts-confirm-delete"
      />
    </div>
  );
}

function TabButton({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`rounded-full border px-4 py-2 text-sm transition-all ${active ? "font-medium" : ""}`}
      style={{
        background: active ? "#1B4332" : "transparent",
        color: active ? "#FAF6EC" : "#1B4332",
        borderColor: active ? "#1B4332" : "rgba(27,67,50,0.2)",
      }}
    >
      {children}
    </button>
  );
}

function ContactCard({ row, onResolve, onDelete }) {
  return (
    <div
      className="rounded-2xl border p-5"
      style={{ background: "#fff", borderColor: "rgba(27,67,50,0.12)" }}
      data-testid={`contact-row-${row.id}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-serif text-lg" style={{ color: "#1B4332" }}>{row.name}</span>
            <span className="text-xs" style={{ color: "#6E6759" }}>&middot; {row.email}</span>
            <span
              className="text-[10px] tracking-[0.18em] uppercase px-2 py-0.5 rounded-full"
              style={{ background: "#FAF6EC", color: "#1B4332", border: "1px solid rgba(27,67,50,0.15)" }}
            >
              {row.topic}
            </span>
            {row.resolved && (
              <span
                className="text-[10px] tracking-[0.18em] uppercase px-2 py-0.5 rounded-full"
                style={{ background: "#1B4332", color: "#C9A84C" }}
              >
                Resolved
              </span>
            )}
          </div>
          <p className="mt-3 text-sm leading-relaxed" style={{ color: "#2A2621" }}>{row.message}</p>
          <div className="mt-3 text-xs" style={{ color: "#6E6759" }}>{formatDate(row.created_at)}</div>
        </div>
        <div className="flex flex-col gap-2 shrink-0">
          <button
            type="button"
            onClick={onResolve}
            className="btn-outline-forest"
            data-testid={`contact-resolve-${row.id}`}
          >
            <Check size={14} /> {row.resolved ? "Reopen" : "Resolve"}
          </button>
          <button
            type="button"
            onClick={onDelete}
            className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm border"
            style={{ borderColor: "#8A2A2A", color: "#8A2A2A" }}
            data-testid={`contact-delete-${row.id}`}
          >
            <Trash2 size={14} /> Delete
          </button>
        </div>
      </div>
    </div>
  );
}

function SignupCard({ row, onDelete }) {
  return (
    <div
      className="rounded-2xl border p-5 flex flex-wrap items-center justify-between gap-3"
      style={{ background: "#fff", borderColor: "rgba(27,67,50,0.12)" }}
      data-testid={`signup-row-${row.id}`}
    >
      <div className="min-w-0">
        <div className="font-serif text-lg" style={{ color: "#1B4332" }}>{row.name}</div>
        <div className="text-xs" style={{ color: "#6E6759" }}>{row.email} &middot; {formatDate(row.created_at)}</div>
      </div>
      <button
        type="button"
        onClick={onDelete}
        className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm border"
        style={{ borderColor: "#8A2A2A", color: "#8A2A2A" }}
        data-testid={`signup-delete-${row.id}`}
      >
        <Trash2 size={14} /> Delete
      </button>
    </div>
  );
}
